import axiosClient from '../axiosClient';
import type { AxiosRequestConfig } from 'axios';
import type {
  DataModelResponse,
  CreateDataModelRequest,
  UpdateDataModelRequest,
  BaseQueryPreviewResponse,
  AggregateFunctionOption,
  RelationCardinalityOption
} from '@/api/contracts/dataModel';
import { assertGuidString } from '@/utils/guid';
import { wireText } from '@/helpers/wireText';

export interface DataModelDeleteDryRunDataViewItem {
  viewId: string;
  name: string;
  isDefault: boolean;
}

export interface DataModelDeleteDryRunResponse {
  modelId: string;
  name: string;
  dataViews: DataModelDeleteDryRunDataViewItem[];
}

/**
 * Bug 2843: henter den backend-eide aggregat-funksjons-katalogen (value + label). Modell-editorens
 * relasjons-dialog rendrer denne i stedet for et hardkodet funksjons-vokabular — samme mønster som
 * `fetchDataViewOperators`. Verdiene er camelCase (matcher en relasjons persisterte `function`).
 */
export async function fetchAggregateFunctions (signal?: AbortSignal): Promise<AggregateFunctionOption[]> {
  const response = await axiosClient.get<AggregateFunctionOption[]>('data-models/aggregate-functions', { signal });
  return response.data;
}

/**
 * Bug 3501: henter den backend-eide kardinalitets-katalogen (value + label + hva som skjer med rader
 * uten treff). Modell-editorens relasjons-dialog rendrer denne i stedet for hardkodede etiketter, saa
 * navnet brukeren leser finnes ett sted. Verdiene er camelCase og normaliseres av kalleren.
 */
export async function fetchRelationCardinalities (signal?: AbortSignal): Promise<RelationCardinalityOption[]> {
  const response = await axiosClient.get<RelationCardinalityOption[]>('data-models/relation-cardinalities', { signal });
  return response.data;
}

// The wire key for a data model table alias is tableId (and fromTableId/toTableId/mainTableId),
// kept that way by the backend through [JsonPropertyName]. The local symbol is alias
// (fromAlias/toAlias/mainAlias). These two functions are the single wire-to-local mapping point for
// all data model I/O, and no contract key gets a chain of ?? fallbacks here.

function normalizeDataModelResponse (model: DataModelResponse): DataModelResponse {
  const legacyAliasKey = ['table', 'PublicId'].join('');
  const raw = model as unknown as Record<string, unknown>;
  return {
    ...model,
    mainAlias: (model.mainAlias ?? (raw.mainTableId as string | null | undefined)) ?? null,
    tables: (model.tables ?? []).map((t) => {
      const table = t as unknown as Record<string, unknown>;
      const tableKey = wireText(table.tableKey || table[legacyAliasKey] || '').trim();
      return {
        ...t,
        alias: wireText(t.alias ?? table.tableId).trim(),
        tableKey,
        columns: t.columns ?? []
      };
    }),
    relations: (model.relations ?? []).map((r) => {
      const rel = r as unknown as Record<string, unknown>;
      return {
        ...r,
        fromAlias: wireText(r.fromAlias ?? rel.fromTableId).trim(),
        toAlias: wireText(r.toAlias ?? rel.toTableId).trim()
      };
    })
  };
}

// Builds the save request by REMOVING the local-only keys from what the caller handed in, never by
// listing the keys worth keeping. A keep-list drops every field the contract gains afterwards, and
// it does so in silence: relation filters and instanceId (WI 3189) and aliasIsUserDefined (US 3500)
// all reached the backend as nothing at all, because each was added to the contract and to the
// editor but not to the list here. The alias renames below are the only deliberate removals, and
// the backend ignores wire members it does not know, so carrying a field it has no use for is free.
function normalizeDataModelRequest<T extends CreateDataModelRequest | UpdateDataModelRequest> (request: T): T {
  const legacyAliasKey = ['table', 'PublicId'].join('');
  const normalizedTables = (request.tables ?? []).map((table) => {
    const raw = table as unknown as Record<string, unknown>;
    const tableKey = wireText(raw.tableKey || raw[legacyAliasKey] || '').trim();
    const normalizedAlias = wireText(raw.alias).trim();
    const normalizedDisplayName = wireText(raw.displayName).trim();
    if (!normalizedAlias) {
      throw new Error('Tabell mangler alias');
    }

    const carried = { ...raw };
    delete carried.alias;
    delete carried[legacyAliasKey];

    return {
      ...carried,
      tableId: normalizedAlias,
      tableKey,
      displayName: normalizedDisplayName,
      columns: table.columns ?? []
    };
  });

  const normalizedRelations = (request.relations ?? []).map((relation) => {
    const raw = relation as unknown as Record<string, unknown>;
    const fromTableId = wireText(raw.fromAlias).trim();
    const toTableId = wireText(raw.toAlias).trim();
    if (!fromTableId) {
      throw new Error('Relasjon mangler fra-tabell');
    }
    if (!toTableId) {
      throw new Error('Relasjon mangler til-tabell');
    }

    const carried = { ...raw };
    delete carried.fromAlias;
    delete carried.toAlias;

    return {
      ...carried,
      fromTableId,
      toTableId
    };
  });

  const { mainAlias, ...rest } = request;
  return {
    ...rest,
    mainTableId: mainAlias ?? null,
    tables: normalizedTables,
    relations: normalizedRelations
  } as unknown as T;
}

export async function getDataModels (signal?: AbortSignal): Promise<DataModelResponse[]> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<DataModelResponse[]>('data-models', config);
  return (response.data ?? []).map(normalizeDataModelResponse);
}

export async function getDataModelById (
  id: string,
  signal?: AbortSignal
): Promise<DataModelResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<DataModelResponse>(`data-models/${assertGuidString(id, 'modelId')}`, config);
  return normalizeDataModelResponse(response.data);
}

export async function createDataModel (
  request: CreateDataModelRequest,
  signal?: AbortSignal
): Promise<DataModelResponse> {
  const config: AxiosRequestConfig = { signal };
  const normalizedRequest = normalizeDataModelRequest(request);
  const response = await axiosClient.post<DataModelResponse>('data-models', normalizedRequest, config);
  return normalizeDataModelResponse(response.data);
}

export async function updateDataModel (
  id: string,
  request: UpdateDataModelRequest,
  signal?: AbortSignal
): Promise<DataModelResponse> {
  const config: AxiosRequestConfig = { signal };
  const normalizedRequest = normalizeDataModelRequest(request);
  const response = await axiosClient.put<DataModelResponse>(`data-models/${assertGuidString(id, 'modelId')}`, normalizedRequest, config);
  return normalizeDataModelResponse(response.data);
}

export async function deleteDataModel (id: string, signal?: AbortSignal): Promise<void> {
  const config: AxiosRequestConfig = { signal };
  await axiosClient.delete(`data-models/${assertGuidString(id, 'modelId')}`, config);
}

export async function getDataModelDeleteDryRun (
  id: string,
  signal?: AbortSignal
): Promise<DataModelDeleteDryRunResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<DataModelDeleteDryRunResponse>(`data-models/${assertGuidString(id, 'modelId')}/delete-dry-run`, config);
  return response.data;
}

export async function getDataModelBaseQueryPreview (
  id: string,
  signal?: AbortSignal
): Promise<BaseQueryPreviewResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<BaseQueryPreviewResponse>(`data-models/${assertGuidString(id, 'modelId')}/base-query-preview`, config);
  return response.data;
}

export async function fixDataModel (id: string, signal?: AbortSignal): Promise<DataModelResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.post<DataModelResponse>(`data-models/${assertGuidString(id, 'modelId')}/fix`, null, config);
  return normalizeDataModelResponse(response.data);
}

export async function regenerateDataModelMaterialization (id: string, signal?: AbortSignal): Promise<DataModelResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.post<DataModelResponse>(`data-models/${assertGuidString(id, 'modelId')}/regenerate-materialization`, null, config);
  return normalizeDataModelResponse(response.data);
}
